import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import { MdClose, MdUploadFile, MdVideocam, MdLink } from 'react-icons/md';
import { HiRefresh } from 'react-icons/hi';
import toastWrapper from '../../../extras/toastify';
import LoadingOverlay from '../../../extras/LoadingOverlay';
import {
  fetchClassRoomVideoV2,
  generateClientLinkAccToServer,
  updateVideoForSpecificClassroom,
  updateVideoForSpecificGroup,
} from '../../../services/apiService';

const ACCOUNT_OPTIONS = [
  { label: 'Google AlmaGrow', value: 'google-almagrow' },
  { label: 'Google Almabetter', value: 'google-almabetter' },
  { label: 'Zoom Ab Class', value: 'zoom-ab-class' },
  { label: 'Zoom Student Success', value: 'zoom-student-success' },
  { label: 'Airmeet', value: 'airmeet' },
];

const labelStyle = {
  fontSize: 12,
  fontWeight: 600,
  color: '#374151',
  marginBottom: 6,
  display: 'block',
};

const inputStyle = {
  width: '100%',
  height: 36,
  border: '1px solid #E5E7EB',
  borderRadius: 4,
  padding: '0 10px',
  fontSize: 13,
  color: '#111827',
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: "'Gellix', 'Inter', sans-serif",
  backgroundColor: '#fff',
};

const selectStyle = {
  ...inputStyle,
  appearance: 'none',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 10px center',
  paddingRight: 30,
  cursor: 'pointer',
};

function RecordingLink({ showModal, setShowModal, data, refreshData, recordingLinkAvail, activeTab }) {
  const videoFileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [videoFile, setVideoFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoLink, setVideoLink] = useState('');
  const [videoKey, setVideoKey] = useState(recordingLinkAvail);
  const [account, setAccount] = useState(activeTab === 'CSS' ? 'google-almagrow' : 'google-almabetter');
  const [uploadMode, setUploadMode] = useState('link'); // 'link' | 'file'

  useEffect(() => {
    if (!videoKey) return;
    const load = async () => {
      try {
        const res = await fetchClassRoomVideoV2(
          data?.classActivityRecording?.videoItemKeyV2?.cloudType,
          data?.classActivityRecording?.videoItemKeyV2?.itemKey
        );
        setVideoUrl(res?.signedUrl);
      } catch (error) {
        console.error(error);
      }
    };
    load();
  }, [videoKey]);

  const handleFileDrop = (e) => {
    e.preventDefault();
    const dropped = e.dataTransfer.files[0];
    if (dropped && dropped.type.startsWith('video/')) setVideoFile(dropped);
  };

  const uploadVideoByFile = async () => {
    const videoExtension = videoFile.name.split('.').pop();
    const res = await generateClientLinkAccToServer({
      fileFormat: 'mp4',
      activityId: data?.activityId,
      cloudType: 'azure',
      account,
    });
    const { signedUrl, videoItemKeyV2 } = res;
    const uploadRes = await axios.put(signedUrl, videoFile, {
      headers: {
        'Content-Type': `video/${videoExtension}`,
        'x-ms-blob-type': 'BlockBlob',
      },
    });
    if ([200, 201].includes(uploadRes.status)) {
      const bodyData = {
        activityId: data?.activityId,
        recording: { type: videoItemKeyV2?.cloudType, link: videoItemKeyV2?.itemKey, account },
      };
      return activeTab === 'LSS'
        ? updateVideoForSpecificClassroom(bodyData)
        : updateVideoForSpecificGroup(bodyData);
    }
  };

  const uploadVideoByLink = async () => {
    const bodyData = {
      activityId: data?.activityId,
      recording: { type: 'external', link: videoLink, account },
    };
    return activeTab === 'LSS'
      ? updateVideoForSpecificClassroom(bodyData)
      : updateVideoForSpecificGroup(bodyData);
  };

  const handleConfirm = async () => {
    if (uploadMode === 'link' && !videoLink.trim()) {
      return toastWrapper('warning', 'Please paste a recording link', 'top-right');
    }
    if (uploadMode === 'file' && !videoFile) {
      return toastWrapper('warning', 'Please select a video file', 'top-right');
    }
    setIsUploading(true);
    try {
      const result = uploadMode === 'file' ? await uploadVideoByFile() : await uploadVideoByLink();
      if (result && !result?.successful) {
        toastWrapper('error', result?.message || 'Upload failed', 'top-right');
        return;
      }
      toastWrapper('success', 'Recording uploaded successfully', 'top-right');
      setShowModal(false);
    } catch (error) {
      console.error(error);
      toastWrapper('error', "Couldn't upload recording", 'top-right');
    } finally {
      setIsUploading(false);
    }
  };

  const canSubmit = uploadMode === 'link' ? !!videoLink.trim() : !!videoFile;

  return (
    <Dialog
      open={showModal}
      onClose={() => setShowModal(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{ style: { borderRadius: 16, overflow: 'hidden' } }}
    >
      {isUploading && <LoadingOverlay />}

      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '18px 24px 14px',
          borderBottom: '1px solid #F3F4F6',
        }}
      >
        <h2 style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: 0 }}>
          {videoKey ? 'View Recording' : 'Upload Recording'}
        </h2>
        <button
          onClick={() => setShowModal(false)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', padding: 4, borderRadius: 4, display: 'flex' }}
          aria-label="Close"
        >
          <MdClose size={20} />
        </button>
      </div>

      <DialogContent style={{ padding: '20px 24px 24px' }}>
        {videoKey ? (
          /* ── View state ── */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div
              style={{
                borderRadius: 8,
                overflow: 'hidden',
                backgroundColor: '#000',
                aspectRatio: '16/9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {videoUrl ? (
                <video src={videoUrl} controls style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, color: '#6B7280' }}>
                  <MdVideocam size={36} />
                  <span style={{ fontSize: 13 }}>Loading preview…</span>
                </div>
              )}
            </div>
            <button
              onClick={() => setVideoKey(false)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                fontSize: 13,
                fontWeight: 600,
                color: '#374151',
                backgroundColor: '#fff',
                border: '1px solid #E5E7EB',
                borderRadius: 4,
                padding: '9px 20px',
                cursor: 'pointer',
                width: '100%',
              }}
            >
              <HiRefresh size={15} />
              Update Recording
            </button>
          </div>
        ) : (
          /* ── Upload state ── */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {/* Account */}
            <div>
              <label style={labelStyle}>Recording Account</label>
              <select style={selectStyle} value={account} onChange={(e) => setAccount(e.target.value)}>
                {ACCOUNT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Mode toggle */}
            <div style={{ display: 'flex', borderRadius: 6, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
              {[
                { key: 'link', icon: <MdLink size={14} />, label: 'Paste Link' },
                { key: 'file', icon: <MdUploadFile size={14} />, label: 'Upload File' },
              ].map((m) => (
                <button
                  key={m.key}
                  onClick={() => setUploadMode(m.key)}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    padding: '8px 0',
                    fontSize: 12,
                    fontWeight: 600,
                    border: 'none',
                    cursor: 'pointer',
                    backgroundColor: uploadMode === m.key ? '#4F46E5' : '#fff',
                    color: uploadMode === m.key ? '#fff' : '#6B7280',
                    transition: 'background-color 150ms ease, color 150ms ease',
                  }}
                >
                  {m.icon}
                  {m.label}
                </button>
              ))}
            </div>

            {/* Link input */}
            {uploadMode === 'link' && (
              <div>
                <label style={labelStyle}>Recording URL</label>
                <input
                  style={inputStyle}
                  placeholder="https://..."
                  value={videoLink}
                  onChange={(e) => setVideoLink(e.target.value)}
                />
              </div>
            )}

            {/* File drop zone */}
            {uploadMode === 'file' && (
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleFileDrop}
                onClick={() => videoFileInputRef.current?.click()}
                style={{
                  border: `2px dashed ${videoFile ? '#4F46E5' : '#E5E7EB'}`,
                  borderRadius: 8,
                  backgroundColor: videoFile ? '#EEF2FF' : '#F9FAFB',
                  padding: '28px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                  cursor: 'pointer',
                  transition: 'border-color 150ms ease, background-color 150ms ease',
                }}
              >
                {videoFile ? (
                  <>
                    <MdVideocam size={32} style={{ color: '#4F46E5' }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#4F46E5', wordBreak: 'break-all', textAlign: 'center' }}>
                      {videoFile.name}
                    </span>
                    <span style={{ fontSize: 11.5, color: '#6B7280' }}>
                      {(videoFile.size / (1024 * 1024)).toFixed(1)} MB · Click to change
                    </span>
                  </>
                ) : (
                  <>
                    <MdUploadFile size={32} style={{ color: '#9CA3AF' }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Click or drag to upload</span>
                    <span style={{ fontSize: 11.5, color: '#9CA3AF' }}>MP4, MOV, AVI supported</span>
                  </>
                )}
                <input
                  ref={videoFileInputRef}
                  type="file"
                  accept="video/*"
                  onChange={(e) => setVideoFile(e.target.files[0])}
                  style={{ display: 'none' }}
                />
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#374151',
                  backgroundColor: '#fff',
                  border: '1px solid #E5E7EB',
                  borderRadius: 4,
                  padding: '9px 20px',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={!canSubmit || isUploading}
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#fff',
                  backgroundColor: '#4F46E5',
                  border: 'none',
                  borderRadius: 4,
                  padding: '9px 20px',
                  cursor: canSubmit && !isUploading ? 'pointer' : 'not-allowed',
                  opacity: canSubmit && !isUploading ? 1 : 0.5,
                  transition: 'opacity 150ms ease',
                }}
              >
                {isUploading ? 'Uploading…' : 'Confirm Upload'}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default RecordingLink;
